import { useState, useEffect } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import type { Group } from '@/hooks/useDashboardData';
import { safeFileName, getFileExtension, MAX_ASSIGNMENT_SIZE } from '@/lib/fileDownload';

interface LessonModalProps {
  open: boolean;
  onClose: () => void;
  defaultGroupId?: string | null;
}

function addMinutes(time: string, minutes: number): string {
  if (!time) return '';
  const [h, m] = time.split(':').map((n) => Number(n));
  const total = h * 60 + m + minutes;
  const hours = Math.floor(total / 60) % 24;
  const mins = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export default function LessonModal({ open, onClose, defaultGroupId = null }: LessonModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupId, setGroupId] = useState('');
  const [status, setStatus] = useState('planlandi');
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function fetchGroups() {
      setLoadingGroups(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('zehrahoca_groups')
          .select('*')
          .eq('active', true)
          .order('created_at', { ascending: true });
        if (fetchError) throw fetchError;
        if (!cancelled) setGroups((data ?? []) as Group[]);
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    }

    fetchGroups();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setGroupId(defaultGroupId ?? '');
      setStatus('planlandi');
      setError(null);
      setSelectedPdf(null);
    } else {
      setError(null);
      setSaving(false);
      setSelectedPdf(null);
    }
  }, [open, defaultGroupId]);

  const selectedGroup = groups.find((g) => g.id === groupId);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const title = (fd.get('lesson_title') as string || '').trim();
    const lessonType = (fd.get('lesson_type') as string || 'grup').trim();
    const subject = (fd.get('subject') as string || '').trim();
    const date = (fd.get('date') as string || '').trim();
    const startTime = (fd.get('start_time') as string || '').trim();
    const duration = Number.parseInt(fd.get('duration') as string || '60', 10);
    const liveLink = (fd.get('live_link') as string || '').trim();
    const description = (fd.get('description') as string || '').trim();
    const pdf = selectedPdf;

    const missing: string[] = [];
    if (!title) missing.push('Ders Başlığı');
    if (!groupId) missing.push('Grup');
    if (!date) missing.push('Tarih');
    if (!startTime) missing.push('Başlangıç Saati');

    if (missing.length > 0) {
      setError(`Lütfen şu alanları doldurun: ${missing.join(', ')}`);
      return;
    }

    // PDF seçildiyse tip ve boyut doğrulaması (kayıttan önce).
    if (pdf && pdf.size > 0) {
      const isPdf =
        pdf.type === 'application/pdf' ||
        ((!pdf.type || pdf.type === 'application/octet-stream') &&
          getFileExtension(pdf.name) === '.pdf');
      if (!isPdf) {
        setError('Lütfen geçerli bir PDF dosyası seçin.');
        return;
      }
      if (pdf.size > MAX_ASSIGNMENT_SIZE) {
        setError("PDF dosyası 20 MB'dan büyük olamaz.");
        return;
      }
    }

    const endTime = addMinutes(startTime, duration);

    setError(null);
    setSaving(true);

    // Ders id'sini önceden üret; hem storage path hem ders kaydı aynı id'yi kullanır.
    const lessonId = crypto.randomUUID();
    let uploadedPath: string | null = null;

    try {
      // 1. PDF varsa önce storage'a yükle (ders kaydından ÖNCE).
      if (pdf && pdf.size > 0) {
        const safeName = safeFileName(pdf.name);
        const path = `lessons/${lessonId}/${Date.now()}-${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(path, pdf, { upsert: true });
        if (uploadError) {
          console.error('[LessonModal] PDF upload failed:', uploadError);
          throw new Error(
            'PDF dosyası yüklenemedi. Lütfen dosya türünü, dosya boyutunu ve internet bağlantınızı kontrol edip tekrar deneyin.',
          );
        }
        uploadedPath = path;
      }

      // 2. Public URL al.
      let publicUrl: string | null = null;
      if (uploadedPath) {
        const { data } = supabase.storage.from('documents').getPublicUrl(uploadedPath);
        publicUrl = data?.publicUrl ?? null;
      }

      // 3. Ders kaydını id = lessonId ile oluştur.
      const { error: insertError } = await supabase.from('zehrahoca_lessons').insert({
        id: lessonId,
        title,
        group_id: groupId,
        level: selectedGroup?.level ?? null,
        subject: subject || null,
        date: date || null,
        start_time: startTime || null,
        end_time: endTime || null,
        type: lessonType,
        live_link: liveLink || null,
        description: description || null,
        status,
        has_document: publicUrl ? true : false,
      });
      if (insertError) {
        console.error('[LessonModal] Lesson insert failed:', insertError);
        throw insertError;
      }

      // 4. PDF varsa doküman kaydını oluştur.
      if (publicUrl) {
        const { error: docError } = await supabase.from('zehrahoca_documents').insert({
          title,
          description: 'Derste paylaşılan PDF dokümanı',
          type: 'pdf',
          lesson_id: lessonId,
          group_id: groupId || null,
          level: selectedGroup?.level ?? null,
          published: true,
          downloadable: true,
          upload_date: new Date().toISOString().slice(0, 10),
          downloads: 0,
          size: `${(pdf!.size / (1024 * 1024)).toFixed(1)} MB`,
          file_url: publicUrl,
        });
        if (docError) {
          console.error('[LessonModal] Document insert failed:', docError);
          throw docError;
        }
      }

      form.reset();
      setSelectedPdf(null);
      window.dispatchEvent(new Event('zehrahoca:lessons-changed'));
      onClose();
    } catch (err) {
      // Upload sonrası ders/doküman kaydı patladıysa yarım kalan dosyayı temizle.
      if (uploadedPath) {
        try {
          await supabase.storage.from('documents').remove([uploadedPath]);
        } catch (cleanupError) {
          console.error('[LessonModal] Cleanup failed:', cleanupError);
        }
      }
      setError((err as Error).message || 'Ders kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Yeni Ders Ekle"
      subtitle="Ders bilgilerini girerek yeni bir ders planlayın."
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Başlığı <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="lesson_title"
              placeholder="Örn: Üslü Sayılar"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Grup <span className="text-red-500">*</span></label>
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="" disabled>
                {loadingGroups ? 'Yükleniyor...' : 'Grup seçin'}
              </option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Seviye (bilgi)</label>
            <input
              type="text"
              value={selectedGroup?.level ?? 'Grup seçince otomatik dolar'}
              disabled
              className="w-full cursor-not-allowed rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-500"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Türü</label>
            <select
              name="lesson_type"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="grup">Grup Dersi</option>
              <option value="birebir">Birebir Ders</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Durumu</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="planlandi">Planlandı</option>
              <option value="islendi">İşlendi</option>
              <option value="ertelendi">Ertelendi</option>
              <option value="iptal">İptal edildi</option>
              <option value="kayit_yuklendi">Kayıt yüklendi</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Konusu</label>
            <input
              type="text"
              name="subject"
              placeholder="Örn: Üslü Sayılar - Temel Kurallar"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Tarih <span className="text-red-500">*</span></label>
            <input
              type="date"
              name="date"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Başlangıç Saati <span className="text-red-500">*</span></label>
            <input
              type="time"
              name="start_time"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Süresi (dk)</label>
            <select
              name="duration"
              defaultValue="60"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="45">45 dk</option>
              <option value="60">60 dk</option>
              <option value="90">90 dk</option>
              <option value="120">120 dk</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Canlı Ders Linki</label>
            <input
              type="url"
              name="live_link"
              placeholder="https://meet.google.com/..."
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Açıklaması</label>
            <textarea
              name="description"
              rows={3}
              maxLength={500}
              placeholder="Bu derste işlenecek konular hakkında kısa bir açıklama..."
              className="w-full resize-none rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">İlgili PDF Doküman Ekle</label>
            <div className="relative flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-background-300 bg-background-50 px-4 py-6 text-sm text-foreground-600 transition hover:border-primary-300 hover:bg-primary-50">
              <i className="ri-upload-cloud-2-line text-xl" />
              {selectedPdf ? (
                <span className="text-foreground-800 font-medium">{selectedPdf.name} ({(selectedPdf.size / (1024 * 1024)).toFixed(1)} MB)</span>
              ) : (
                <span>PDF dosyası seçmek için tıklayın</span>
              )}
              <input
                type="file"
                name="pdf"
                accept=".pdf"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                onChange={(e) => setSelectedPdf(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
          >
            {saving ? 'Kaydediliyor...' : 'Dersi Kaydet'}
          </button>
        </div>
      </form>
    </Modal>
  );
}